import { ContentKey } from "../TalentDetails";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectTalentById,
  updateTalentEducations,
} from "../../talents/talentsSlice";
import {
  Educations,
  Talent,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import { useParams } from "react-router-dom";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import { useTranslation } from "react-i18next";
import { Backdrop, Box, capitalize, Grid } from "@mui/material";
import { format } from "date-fns";
import getLocale from "../../../common/helpers/getLocale";
import FormTable, {
  DataItem,
} from "../../../common/components/functional/formTable/FormTable";
import isEmpty from "../../../common/helpers/isEmpty";
import { useContext, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { AccessRightContext } from "../../user/AccessRightProvider";

type Props = {
  handleOpenEditModal: (content: ContentKey, id?: string) => void;
};

const nominative = (date: string) => {
  return capitalize(
    format(new Date(date), "LLLL yyyy", { locale: getLocale() }),
  );
};

const TalentEducations = ({ handleOpenEditModal }: Props) => {
  const { talentId } = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const educations: Educations | undefined = talent.educations;
  const values = educations?.values;

  const formattedData: DataItem[] = values
    ? values
        .map(({ id, degree, school, start, end, industry }) => ({
          values: {
            degree: degree,
            school: (
              <>
                <Box>{[school, industry].join(", ")}</Box>
                <Box>
                  {nominative(start)} - {end ? nominative(end) : ""}
                </Box>
              </>
            ),
          },
          id: id,
          deletable: true,
        }))
        .sort((a, b) => a.values.degree.localeCompare(b.values.degree)) // Sort alphabetically
    : [];

  const deleteEducation = (id?: string) => {
    const currentValues = talent.educations?.values || [];
    const currentVersion = talent.educations?.version || 0;

    if (talentId && !isEmpty(currentValues) && currentVersion) {
      setIsLoading(true);

      const newValues = currentValues.filter((item) => item.id !== id);

      dispatch(
        updateTalentEducations({
          talentId: talentId,
          educations: newValues,
          version: currentVersion,
        }),
      ).then((action) => {
        setIsLoading(false);
        return isFulfilled(action);
      });
    }
  };

  const headers = [
    { text: t("education:degree") },
    { text: t("education:school") },
  ];
  const addButtonLabel = t("education:add");
  const title = t("education:education");

  return (
    <FormSection title={title} margin>
      <Grid container columnSpacing={2}>
        <Backdrop
          open={isLoading}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.4)",
            zIndex: 1000,
          }}
        >
          <LoadingSpinner />
        </Backdrop>
        <Grid
          item
          container
          xs={3}
          justifyContent="flex-end"
          alignItems="center"
        />
        <Grid item xs={9}>
          <FormTable
            data={formattedData}
            headers={headers}
            handleOpenEditModal={handleOpenEditModal}
            handleDelete={deleteEducation}
            contentKey="educations"
            addButtonLabel={addButtonLabel}
            disableEditRow={
              !canEditTalentProfile || talentIsDeactivated(talent)
            }
          />
        </Grid>
      </Grid>
    </FormSection>
  );
};

export default TalentEducations;
